"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var LazyLoading_1 = __importDefault(require("./LazyLoading"));
var Orientation;
(function (Orientation) {
    Orientation[Orientation["Vertical"] = 0] = "Vertical";
    Orientation[Orientation["Horizontal"] = 1] = "Horizontal";
})(Orientation = exports.Orientation || (exports.Orientation = {}));
/**
 * A collection view
 *
 * Features:
 * - pluggable layouts
 * - arbitrary custom item views
 * - lazy loading/unloading
 *
 * Usage: instantiate, render, update
 */
var ListView = /** @class */ (function () {
    /**
     * @param {T[]} items
     * @param {Layout} layout
     */
    function ListView(items, layout, renderer) {
        this.items = items;
        this.layout = layout;
        this.renderer = renderer;
        this.root = null;
        this.content = null;
        this.lazyLoading = null;
        this.views = [];
        this.viewsById = new Map();
    }
    /**
     * Destroy item views and free memory
     */
    ListView.prototype.destroy = function () {
        if (this.lazyLoading) {
            this.lazyLoading.destroy();
            this.lazyLoading = null;
        }
        if (this.layoutRequest) {
            cancelAnimationFrame(this.layoutRequest);
            this.layoutRequest = null;
        }
        if (this.updateRequest) {
            cancelAnimationFrame(this.updateRequest);
            this.updateRequest = null;
        }
        this.viewsById.clear();
        this.views = [];
        this.items = [];
        this.root = null;
    };
    /**
     * Generate a DOM tree with a lazy-loaded dynamic list of items
     */
    ListView.prototype.render = function (viewSize, preparedRoot) {
        var _this = this;
        if (preparedRoot === void 0) { preparedRoot = null; }
        var root = preparedRoot || document.createElement('div');
        root.style.position = 'relative';
        root.style.setProperty('-webkit-overflow-scrolling', 'touch'); // Smooth scrolling on iOS
        this.root = root;
        this.viewportSize = viewSize;
        this.lazyLoading = new LazyLoading_1.default(root);
        this.content = document.createElement('div');
        root.appendChild(this.content);
        this.applyViewSize(viewSize);
        // Wait until the root element is laid out
        this.layoutRequest = requestAnimationFrame(function () { return _this.setLazyItems(_this.items); });
        return root;
    };
    /**
     * Update settings
     * Performs settings update atomically,running necessary view updates only at the end of the transaction
     *
     * This method passes a set of update functions into the operation callback. So that from inside of the callback
     * client can update viewSize, layout, etc.
     */
    ListView.prototype.update = function (operation) {
        var _this = this;
        var relayoutNeeded = false;
        var newItems;
        var handler = {
            updateSize: function (viewSize) {
                _this.viewportSize = viewSize;
                _this.applyViewSize(viewSize);
                relayoutNeeded = true; // TODO Relayout is not needed if resize happened in the direction of the scroll
            },
            updateLayout: function (layout) {
                _this.layout = layout;
                relayoutNeeded = true;
            },
            updateItems: function (items) {
                newItems = items;
            }
        };
        operation(handler);
        if (this.root) {
            if (newItems) {
                this.setLazyItems(newItems);
            }
            else if (relayoutNeeded) {
                this.applyLayout(this.viewportSize);
                if (this.lazyLoading) {
                    // TODO Just recalculate activeArea + manage
                    this.updateRequest = requestAnimationFrame(function () { return _this.lazyLoading.refreshItems(); });
                }
            }
        }
    };
    ListView.prototype.setLazyItems = function (newItems) {
        var _this = this;
        var newViewMap = new Map();
        this.views = newItems.map(function (item) {
            var view = _this.viewsById.get(item.id);
            if (view) {
                view.update && view.update(item);
            }
            else {
                view = _this.renderer(item, _this.content);
            }
            newViewMap.set(item.id, view);
            return view;
        });
        this.viewsById = newViewMap;
        this.items = newItems;
        this.applyLayout(this.viewportSize);
        this.lazyLoading.setItemViews(this.views);
    };
    ListView.prototype.applyViewSize = function (viewSize) {
        this.root.style.width = viewSize.width + 'px';
        this.root.style.height = viewSize.height + 'px';
        if (this.layout.orientation === Orientation.Vertical) {
            this.content.style.width = viewSize.width + 'px';
        }
        else {
            this.content.style.height = viewSize.height + 'px';
        }
    };
    ListView.prototype.applyLayout = function (viewportSize) {
        if (!this.root)
            return;
        var rects = this.layout.layout(this.items.map(function (i) { return i.size; }), viewportSize);
        this.views.forEach(function (view, index) { return view.setGeometry(rects[index]); });
        if (this.layout.orientation === Orientation.Vertical) {
            this.root.style.overflowY = 'scroll';
            this.root.style.overflowX = 'hidden';
            var contentHeight = rects.reduce(function (acc, r) { return Math.max(acc, r.y + r.height); }, 0);
            this.content.style.height = contentHeight + 'px';
        }
        else {
            this.root.style.overflowX = 'scroll';
            this.root.style.overflowY = 'hidden';
            var contentWidth = rects.reduce(function (acc, r) { return Math.max(acc, r.x + r.width); }, 0);
            this.content.style.width = contentWidth + 'px';
        }
    };
    return ListView;
}());
exports.ListView = ListView;
